import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Send, Loader2, Plus, Trash2, RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react';
import KruozAPI from '../../KruozAPI';

/*
	ChatAndQuoate (Chat + Quote) Component
	-------------------------------------------------
	PURPOSE
	A conversational interface that incrementally gathers the information needed to produce an asset quotation, 
	shows a live-updating Quote panel, and lets the user refine requirements via natural language.

	FEATURES
	- Chat thread (user & assistant messages)
	- Simple rule-based requirement extraction (dates, quantity, asset keywords) with extensible LLM placeholder
	- Auto-prompts for missing required fields (deliveryDate, collectionDate)
	- Fetch assets availability via endpoint: Assets/AssetForBookingByDateRange
	- Asset selection & quantity adjustments mirrored into the quotation panel
	- User can modify quote by messages like "add 2 large bins" or "remove the small bin" or "change delivery to 3 Oct"
	- Extensible placeholder for OpenAI LLM (plug in real call where indicated)

	REQUIRED ENV / INTEGRATION NOTES
	- Replace the placeholder LLM function `callLLM` with actual OpenAI or backend call.
	- companyId is accepted as prop or can be taken from logged in user (headers already handled by interceptor).
*/

const INITIAL_ASSISTANT_MESSAGE = `Hi! I can build a quotation for you. Tell me what you need and I'll guide you.\n\nTo start, please provide:\n• Desired delivery date (e.g. 2025-10-03)\n• Collection date (if known)\n• Asset type(s) and approximate quantities (e.g. 2 x 4m skip bins)\nYou can change anything later by just telling me in chat.`;

// Regex helpers (simple; can be expanded)
const DATE_REGEX = /(?:\b|)(20\d{2})[-\/.](0?[1-9]|1[0-2])[-\/.](0?[1-9]|[12]\d|3[01])\b/g; // yyyy-mm-dd variants
const QUANTITY_ASSET_REGEX = /(add|need|want|require|remove|delete)?\s*(\d+)\s*(x)?\s*([a-zA-Z0-9\- ]+?)(?:s\b|\b)/i;
const CHANGE_DELIVERY_REGEX = /change (?:the )?delivery (?:date )?to ([0-9]{4}-[0-9]{2}-[0-9]{2})/i;
const CHANGE_COLLECTION_REGEX = /change (?:the )?collection (?:date )?to ([0-9]{4}-[0-9]{2}-[0-9]{2})/i;

// Minimal asset keyword mapping (expand as needed)
const ASSET_KEYWORDS = [
	'skip bin','bin','mini bin','wheelie bin','coolroom','freezer','container','crane','truck','generator'
];

// Placeholder: determine if message is about adding/removing assets
const parseAssetInstruction = (text) => {
	const m = text.match(QUANTITY_ASSET_REGEX);
	if (!m) return null;
	const intent = (m[1]||'add').toLowerCase();
	const quantity = parseInt(m[2],10);
	let label = (m[4]||'').trim().toLowerCase();
	// Attempt keyword normalization
	const matchedKeyword = ASSET_KEYWORDS.find(k => label.includes(k));
	if (matchedKeyword) label = matchedKeyword;
	return { intent: intent.includes('remove')||intent.includes('delete') ? 'remove' : 'add', quantity, label };
};

// Extract first 2 dates as delivery & collection (simple heuristic)
const extractDates = (text) => {
	const dates = Array.from(text.matchAll(DATE_REGEX)).map(m => `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`);
	return dates;
};

// ChatGPT (OpenAI) integration. Uses fetch directly from browser – for production, proxy via server to hide API key.
// Required env var: REACT_APP_OPENAI_API_KEY (NOT recommended to expose in frontend for production use!)
// Optional env vars: REACT_APP_OPENAI_MODEL (default gpt-4o-mini) / REACT_APP_OPENAI_BASE_URL (default https://api.openai.com)
async function callLLM(conversation, requirements) {
	const missing = [];
	if (!requirements.deliveryDate) missing.push('delivery date');
	if (!requirements.collectionDate) missing.push('collection date');

	const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
	if (!apiKey) {
		// Fallback lightweight guidance if key not present
		let fallback;
		if (missing.length) fallback = `I still need your ${missing.join(' and ')}.`; else fallback = 'You can now add or adjust assets (e.g. "add 2 freezers").';
		return { content: fallback, meta: { missing, provider: 'fallback' } };
	}

	const baseUrl = process.env.REACT_APP_OPENAI_BASE_URL || 'https://api.openai.com';
	const model = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';

	const systemPrompt = `You are a quotation assistant. Extract structured intents: dates (delivery & collection), asset add/remove with quantities, and clarifying questions. Keep replies concise (<=80 words). If key info missing, clearly ask for it. DO NOT hallucinate prices. Just guide user.`;

	try {
		const openAIMessages = [
			{ role: 'system', content: systemPrompt },
			...conversation.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content }))
		];
		const body = {
			model,
			messages: openAIMessages,
			temperature: 0.4,
			max_tokens: 220
		};
		const res = await fetch(`${baseUrl}/v1/chat/completions`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${apiKey}`
			},
			body: JSON.stringify(body)
		});
		if (!res.ok) throw new Error('OpenAI error');
		const json = await res.json();
		const content = json.choices?.[0]?.message?.content?.trim() || 'I am ready.';
		return { content, meta: { provider: 'openai', missing } };
	} catch (e) {
		console.error('LLM error', e);
		let fallback;
		if (missing.length) fallback = `I still need your ${missing.join(' and ')}.`; else fallback = 'Feel free to add or adjust assets.';
		return { content: fallback, meta: { provider: 'error-fallback', missing } };
	}
}

const ChatAndQuoate = ({ companyId = '8420632A-6D48-4E58-A2C9-45F0D68E9443' }) => {
	const [messages, setMessages] = useState([
		{ id: 1, role: 'assistant', content: INITIAL_ASSISTANT_MESSAGE, ts: Date.now() }
	]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [assets, setAssets] = useState([]); // fetched raw items (each physical instance)
	const [groupedAssets, setGroupedAssets] = useState([]); // aggregated logical assets
	const [quoteAssets, setQuoteAssets] = useState([]); // {label, key, quantity, unitPrice}
	const [requirements, setRequirements] = useState({
		deliveryDate: '',
		collectionDate: '',
	});
	const [fetchingAssets, setFetchingAssets] = useState(false);
	const [showGroupExpanded, setShowGroupExpanded] = useState({});
	const chatEndRef = useRef(null);

	const scrollToBottom = useCallback(() => {
		if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
	}, []);

	useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

	const addMessage = useCallback((role, content) => {
		setMessages(prev => [...prev, { id: prev.length+1, role, content, ts: Date.now() }]);
	}, []);

	const parseUserMessage = useCallback((text) => {
		const updates = {};
		// Dates
		const foundDates = extractDates(text);
		if (foundDates.length > 0) {
			if (!requirements.deliveryDate) updates.deliveryDate = foundDates[0];
			if (foundDates.length > 1 && !requirements.collectionDate) updates.collectionDate = foundDates[1];
		}
		// Intent-based date change
		const changeDel = text.match(CHANGE_DELIVERY_REGEX);
		if (changeDel) updates.deliveryDate = changeDel[1];
		const changeCol = text.match(CHANGE_COLLECTION_REGEX);
		if (changeCol) updates.collectionDate = changeCol[1];
		// Asset add/remove
		const assetInstruction = parseAssetInstruction(text);
		return { updates, assetInstruction };
	}, [requirements]);

	const handleAssetInstruction = useCallback((instruction) => {
		if (!instruction) return;
		setQuoteAssets(prev => {
			const existingIndex = prev.findIndex(a => a.label === instruction.label);
			if (instruction.intent === 'add') {
				if (existingIndex >= 0) {
					const copy = [...prev];
						copy[existingIndex] = { ...copy[existingIndex], quantity: copy[existingIndex].quantity + instruction.quantity };
					return copy;
				}
				return [...prev, { label: instruction.label, quantity: instruction.quantity }];
			} else {
				// remove intent => decrement or remove
				if (existingIndex >= 0) {
					const copy = [...prev];
					const newQty = copy[existingIndex].quantity - instruction.quantity;
					if (newQty <= 0) copy.splice(existingIndex,1); else copy[existingIndex] = { ...copy[existingIndex], quantity: newQty };
					return copy;
				}
				return prev; // nothing to remove
			}
		});
	}, []);

	const calculateDays = useCallback(() => {
		if (!requirements.deliveryDate || !requirements.collectionDate) return 0;
		const d1 = new Date(requirements.deliveryDate);
		const d2 = new Date(requirements.collectionDate);
		if (isNaN(d1) || isNaN(d2)) return 0;
		const diff = (d2 - d1) / (1000*60*60*24);
		return diff > 0 ? Math.ceil(diff) : 0; // assume collection is after delivery, exclude same-day until business rule clarified
	}, [requirements.deliveryDate, requirements.collectionDate]);

	const safeEvalEquation = (equation, ctx) => {
		// Very constrained evaluation: provide only expected vars.
		// NOTE: For security, move this to backend if users can edit equations.
		try {
			const fn = new Function('days','noOfItems','distance', `${equation}`);
			return fn(ctx.days, ctx.noOfItems, ctx.distance);
		} catch (e) {
			console.warn('Price equation eval failed', e);
			return null;
		}
	};

	const computeUnitPrice = useCallback((asset, days, distance=0) => {
		if (!asset?.PriceEquation || days<=0) return null;
		const totalForOne = safeEvalEquation(asset.PriceEquation, { days, noOfItems: 1, distance });
		if (totalForOne == null) return null;
		return Number(totalForOne); // already covers period
	}, []);

	const groupAssets = useCallback((raw, days) => {
		const map = new Map();
		raw.forEach(item => {
			const key = `${item.AssetTypeName || 'Unknown'}|${item.AssetGroupName || 'Group'}`;
			if (!map.has(key)) {
				map.set(key, {
					key,
					assetTypeName: item.AssetTypeName,
					groupName: item.AssetGroupName,
					description: item.AssetGroupDescription || item.Description || '',
					items: [],
					availableCount: 0,
					prototype: item // sample for pricing/equations
				});
			}
			const group = map.get(key);
			group.items.push(item);
			if (item.IsAvailable) group.availableCount += 1;
		});
		// compute pricing
		return Array.from(map.values()).map(g => {
			const unitPrice = computeUnitPrice(g.prototype, days, 0);
			return { ...g, unitPrice };
		}).sort((a,b)=> (b.availableCount - a.availableCount));
	}, [computeUnitPrice]);

	const fetchAssets = useCallback(async (force=false) => {
		if (!requirements.deliveryDate || !requirements.collectionDate) return;
		if (assets.length>0 && !force) return; // don't refetch unless forced
		setFetchingAssets(true);
		try {
			const response = await KruozAPI.get('Assets/AssetForBookingByDateRangeAndProperties', {
				params: {
					startDate: requirements.deliveryDate,
					endDate: requirements.collectionDate,
					companyId: companyId,
					properties:'123'
				}
			});
			const raw = response.data || [];
			setAssets(raw);
			const days = calculateDays();
			setGroupedAssets(groupAssets(raw, days));
		} catch (err) {
			console.error('Error fetching assets', err);
			addMessage('assistant', 'I had trouble fetching asset availability. You can retry with "refresh assets".');
		} finally {
			setFetchingAssets(false);
		}
	}, [requirements.deliveryDate, requirements.collectionDate, companyId, assets.length, addMessage, calculateDays, groupAssets]);

	useEffect(() => { fetchAssets(); }, [requirements.deliveryDate, requirements.collectionDate, fetchAssets]);

	// Recompute group pricing if dates change (days changed)
	useEffect(() => {
		if (assets.length === 0) return;
		const days = calculateDays();
		setGroupedAssets(groupAssets(assets, days));
	}, [calculateDays, assets, groupAssets]);

	// Re-evaluate quote asset unit prices when days/grouped assets change
	useEffect(() => {
		if (groupedAssets.length === 0) return;
		setQuoteAssets(prev => prev.map(q => {
			const g = groupedAssets.find(gp => gp.key === q.key || gp.key === q.label);
			if (!g) return q;
			return { ...q, unitPrice: g.unitPrice };
		}));
	}, [groupedAssets]);

	const handleSend = async () => {
		if (!input.trim()) return;
		const userText = input.trim();
		setInput('');
		addMessage('user', userText);
		setLoading(true);
		try {
			if (/refresh assets/i.test(userText)) {
				await fetchAssets(true);
			}
			const { updates, assetInstruction } = parseUserMessage(userText);
			if (Object.keys(updates).length>0) {
				setRequirements(prev => ({ ...prev, ...updates }));
				if (updates.deliveryDate || updates.collectionDate) {
					addMessage('assistant', `Updated ${Object.keys(updates).join(' & ')}.`);
				}
			}
			if (assetInstruction) {
				handleAssetInstruction(assetInstruction);
				addMessage('assistant', `${assetInstruction.intent === 'add' ? 'Added' : 'Updated'} ${assetInstruction.quantity} x ${assetInstruction.label}.`);
			}
			// LLM guidance
			const llm = await callLLM(messages.concat({ role:'user', content:userText }), { ...requirements, ...updates });
			if (llm?.content) addMessage('assistant', llm.content);
		} catch (e) {
			console.error(e);
			addMessage('assistant', 'Something went wrong processing that. Try rephrasing.');
		} finally {
			setLoading(false);
		}
	};

	const subtotal = useMemo(() => {
		return quoteAssets.reduce((sum,a)=> sum + (a.unitPrice || 0) * a.quantity, 0);
	}, [quoteAssets]);

	const readyForQuote = requirements.deliveryDate && requirements.collectionDate && quoteAssets.length>0;

	const updateQuoteAssetQty = (label, delta) => {
		setQuoteAssets(prev => prev.map(a => a.label===label ? { ...a, quantity: Math.max(1, a.quantity+delta)}:a));
	};

	const removeQuoteAsset = (label) => {
		setQuoteAssets(prev => prev.filter(a => a.label!==label));
	};

	return (
		<div className="w-full h-full flex flex-col lg:flex-row gap-4">
			{/* Chat Panel */}
			<div className="flex-1 flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
				<div className="px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-gray-700">Chat Quotation Assistant</h2>
					<div className="text-xs text-gray-500">Experimental</div>
				</div>
				<div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
					{messages.map(m => (
						<div key={m.id} className={`flex ${m.role==='user' ? 'justify-end':'justify-start'}`}>
							<div className={`max-w-[75%] rounded-lg px-3 py-2 shadow-sm leading-relaxed whitespace-pre-wrap ${m.role==='user' ? 'bg-blue-600 text-white':'bg-gray-100 text-gray-800'}`}>
								{m.content}
							</div>
						</div>
					))}
					{loading && (
						<div className="flex justify-start">
							<div className="flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-lg text-gray-600 text-xs">
								<Loader2 className="w-4 h-4 animate-spin" />
								<span>Thinking...</span>
							</div>
						</div>
					)}
					<div ref={chatEndRef} />
				</div>
				<div className="p-3 border-t bg-gray-50">
					<div className="flex items-end gap-2">
						<textarea
							value={input}
							onChange={(e)=>setInput(e.target.value)}
							rows={2}
							placeholder="Describe needs or modify (e.g. add 2 skip bins, change delivery to 2025-10-03)"
							className="flex-1 text-sm resize-none rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 p-2"
							disabled={loading}
						/>
						<button
							onClick={handleSend}
							disabled={loading || !input.trim()}
							className="h-10 px-4 rounded-md bg-blue-600 text-white text-sm font-medium flex items-center gap-1 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
						</button>
					</div>
					<div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-500">
						<span>Shortcuts:</span>
						<button onClick={()=>setInput('add 2 skip bins')} className="underline hover:text-blue-600">add 2 skip bins</button>
						<button onClick={()=>setInput('change delivery date to 2025-10-05')} className="underline hover:text-blue-600">change delivery date ...</button>
						<button onClick={()=>setInput('refresh assets')} className="underline hover:text-blue-600">refresh assets</button>
					</div>
				</div>
			</div>

			{/* Quote / Assets Side Panel */}
			<div className="w-full lg:w-96 flex flex-col gap-4">
				<div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
					<div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-gray-700">Requirements</h3>
						<button
							onClick={()=> fetchAssets(true)}
							title="Force refresh"
							className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
						>
							{fetchingAssets ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
						</button>
					</div>
					<div className="p-4 text-xs space-y-2">
						<div className="flex justify-between"><span className="text-gray-500">Delivery Date</span><span className="font-medium">{requirements.deliveryDate || '—'}</span></div>
						<div className="flex justify-between"><span className="text-gray-500">Collection Date</span><span className="font-medium">{requirements.collectionDate || '—'}</span></div>
						<div className="pt-2 border-t text-[11px] text-gray-500">Update via chat: "change delivery to 2025-10-05"</div>
					</div>
				</div>

				<div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
					<div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-gray-700">Available Assets {fetchingAssets && <Loader2 className="w-4 h-4 ml-2 animate-spin inline" />}</h3>
					</div>
					<div className="p-3 h-56 overflow-y-auto space-y-2 text-xs">
						{(!requirements.deliveryDate || !requirements.collectionDate) && (
							<p className="text-gray-500 text-[11px]">Provide delivery & collection dates to load availability.</p>
						)}
						{requirements.deliveryDate && requirements.collectionDate && groupedAssets.length===0 && !fetchingAssets && (
							<p className="text-gray-500 text-[11px]">No assets fetched yet. Try refining requirements or "refresh assets".</p>
						)}
						{groupedAssets.map(g => {
							const expanded = showGroupExpanded[g.key];
							return (
								<div key={g.key} className="border rounded-md p-2 flex flex-col gap-1 hover:border-blue-300 transition">
									<div className="flex items-start justify-between gap-2">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-1">
												<button
													onClick={()=> setShowGroupExpanded(prev => ({ ...prev, [g.key]: !expanded }))}
													className="p-1 rounded hover:bg-gray-100 text-gray-500"
												>
													{expanded ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
												</button>
												<span className="font-medium text-gray-700 text-[12px] truncate" title={`${g.assetTypeName} - ${g.groupName}`}>{g.assetTypeName} - {g.groupName}</span>
											</div>
											<div className="text-[10px] text-gray-500 mt-0.5 line-clamp-2">{g.description || 'No description'}</div>
											<div className="flex flex-wrap gap-2 mt-1 text-[10px] text-gray-600">
												<span className="px-2 py-0.5 bg-gray-100 rounded">Avail: {g.availableCount}</span>
												{g.unitPrice != null && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">Est: ${g.unitPrice.toFixed(2)}</span>}
											</div>
										</div>
										<button
											onClick={()=> setQuoteAssets(prev=> {
												const label = g.key; // unique key label
												const existing = prev.find(p=>p.key===g.key);
												if (existing) return prev.map(p=> p.key===g.key ? { ...p, quantity: p.quantity+1 }:p);
												return [...prev, { label, key: g.key, quantity:1, unitPrice: g.unitPrice }];
											})}
											className="text-[10px] h-fit px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
										><Plus className="w-3 h-3"/>Add</button>
									</div>
									{expanded && (
										<div className="mt-2 grid grid-cols-2 gap-1">
											{g.items.slice(0,6).map(it => (
												<div key={it.Id} className="text-[10px] px-2 py-1 bg-gray-50 rounded flex justify-between">
													<span className="truncate" title={it.Name}>{it.Code || it.Name}</span>
													{!it.IsAvailable && <span className="text-red-500 ml-1">✕</span>}
												</div>
											))}
											{g.items.length > 6 && <div className="text-[10px] text-gray-400 px-1">+{g.items.length-6} more</div>}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col">
					<div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-gray-700">Current Quote</h3>
						{quoteAssets.length>0 && (
							<span className="text-[10px] text-gray-500">{quoteAssets.length} line{quoteAssets.length>1?'s':''}</span>
						)}
					</div>
					<div className="p-3 h-56 overflow-y-auto text-xs space-y-2">
						{quoteAssets.length===0 && (
							<p className="text-gray-500 text-[11px]">No assets in quote. Use chat or Add buttons.</p>
						)}
						{quoteAssets.map((qa,i)=>(
							<div key={i} className="border rounded-md p-2 flex flex-col gap-1">
								<div className="flex justify-between items-center">
									<span className="font-medium text-gray-700 text-[12px] truncate" title={qa.label}>{qa.label.replace('|',' - ')}</span>
									<div className="flex items-center gap-1">
										<button onClick={()=>updateQuoteAssetQty(qa.label,-1)} className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[11px]">-</button>
										<span className="text-[11px] w-5 text-center">{qa.quantity}</span>
										<button onClick={()=>updateQuoteAssetQty(qa.label,1)} className="px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[11px]">+</button>
										<button onClick={()=>removeQuoteAsset(qa.label)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3"/></button>
									</div>
								</div>
								<div className="flex justify-between text-[10px] text-gray-500">
									<span>Est (period)</span>
									<span>{qa.unitPrice!=null ? `$${qa.unitPrice.toFixed(2)}` : '—'}</span>
								</div>
							</div>
						))}
					</div>
					<div className="p-3 border-t bg-gray-50 space-y-2 text-[11px]">
						<div className="flex justify-between"><span className="text-gray-500">Subtotal (est.)</span><span className="font-medium">{subtotal>0?`$${subtotal.toFixed(2)}`:'—'}</span></div>
						<div className="text-[10px] text-gray-500">Estimates from asset equations (period total per unit x quantity). Transport not included.</div>
						{readyForQuote ? (
							<button className="w-full px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium">Proceed / Finalize (placeholder)</button>
						) : (
							<div className="text-[10px] text-amber-600">Provide dates & add assets to finalize.</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

ChatAndQuoate.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default ChatAndQuoate;

